import {ErrorMessage} from 'formik';
import React from 'react';

import {FormContainer} from '../../form-containers/FormGroup';
import {FormSelectListGlobalStyle} from '../../form-control-features/FormSelectMenuList';
import {FormErrorMessage} from '../../form-labels/FormErrorMessage';
import {FormikSelect} from './FormikSelect';
import {RawSelect} from './RawSelect';

const CustomDropdownIndicator = () => null;
const CustomIndicatorSeparator = () => null;

/**
 * @template {string | ReactNode} TLabel
 *
 * @param {object} param0
 *
 * @param {boolean} [param0.autoFocus]
 * Whether the select should automatically get focus when the page loads.
 *
 * @param {boolean} [param0.controlShouldRenderValue]
 * Whether the value of the select, e.g. SingleValue, should be displayed in the control.
 *
 * @param {{label: string; value: any} | Array<{label: string; value: any}>} [param0.defaultValue]
 * Specifies the initially selected option(s).
 *
 * @param {boolean} [param0.disabled]
 * Whether the select is disabled.
 *
 * @param {(option: {label: string; value: any; data?: object}, search: string) => boolean} [param0.filterOption]
 * This function is called for each option and provides the option as well as the current search input.
 * It allows you to implement custom logic that decides whether an option should be displayed in the menu.
 *
 * @param {boolean} [param0.hideSelectedOptions]
 * Specifies whether to hide the selected option from the menu.
 *
 * @param {React.ReactNode} [param0.statusBadge]
 * Specifies an extra label element to be displayed on the right side of the `Select`.
 * Use the JSX syntax to include an icon element along with some text. For example: {<Icon name='edit' />} or {<>Custom text <Icon name='edit' /></>}
 *
 * @param {any} [param0.innerRef]
 * A reference that will be linked to the underlying select.
 *
 * @param {boolean} [param0.isClearable]
 * Is the select value clearable.
 *
 * @param {boolean} [param0.isMulti]
 * Specifies whether the select should allow multiple values to be selected.
 *
 * @param {boolean} [param0.isSearchable]
 * Whether to enable search functionality.
 *
 * @param {boolean} [param0.menuIsOpen]
 * Controls whether the dropdown menu is open (true) or closed (false) in Select.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {string} [param0.name]
 * *This prop is applicable only for the Formik version of the control.*
 *
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name prop to a Formik select component is essential because Formik uses the name prop to link the value of the select input to the Formik state.
 *
 * **Note: Formik is the default type injected into the form control.**
 *
 * @param {Array<{label: TLabel; value: any; isDisabled?: boolean;}>} param0.options
 * An array of objects with 'label' for display text and 'value' for option value.
 *
 * @param {string} [param0.placeholder]
 * The text displayed in the Select when it is empty and not focused, providing a hint to the user about what information is expected.
 * Placeholder text follows a naming convention for consistency:
 *   - 'Add filter'
 *   - 'Type your name'
 *   - 'Select an option'
 *   - 'Pick an option'
 *   - 'Fill in your name'
 *
 * Avoid using placeholders like:
 *   - '--- type ---'
 *   - '--- select ---'
 *
 * @param {React.ReactNode} [param0.prefix]
 * The text or icons to be displayed before the Select. Icons can be added using the following format:
 * prefix={<><Icon name='edit' /> -- additional text --</>}
 *
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be injected instead.
 *
 * **Note: Formik is the default type injected into the form control.**
 *
 * @param {"sm"} [param0.size]
 * CSS variable names for adjusting the size of the form control you're using:
 *   - 'sm': Smaller variant of the form control type.
 *
 * @param {React.ReactNode} [param0.suffix]
 * The text or icons to be displayed after the Select. Icons can be added using the following format:
 * suffix={<><Icon name='edit' /> -- additional text --</>}
 *
 * @param {string} [param0.validationError]
 * Error message to display when select validation fails
 *
 * @param {any} [param0.value]
 * *This prop is applicable only for the non-Formik version of the control.*
 *
 * The selected value, or multiple values if `isMulti` is set to `true`.
 *
 * @param {(option: {label: TLabel; value: any} | Array<{label: TLabel; value: any}> | null, action: import("react-select").ActionMeta<any>) => void} [param0.onChange]
 * Callback function to handle change events triggered by the select component.
 * Function to be called when a change event occurs.
 *
 * @param {React.FocusEventHandler<HTMLInputElement>} [param0.onFocus]
 * Callback function to handle focus events triggered by the component.
 * Function to be called when the component receives focus.
 *
 * @param {React.FocusEventHandler<HTMLInputElement>} [param0.onBlur]
 * Callback function to handle blur events triggered by the component.
 * Function to be called when the component loses focus.
 *
 * @param {object} [param0.components]
 * Object containing custom components to override the default components of the select control.
 * For reference to the component, view here: https://react-select.com/components#replacing-components
 *
 * @example
 * ```jsx
 * <Select
 *     name="trigger.method"
 *     label="HTTP Method"
 *     options={[
 *         {key: 'Pick a method', value: ''},
 *         {key: 'GET', value: 'GET'},
 *         {key: 'POST', value: 'POST'},
 *         {key: 'PUT', value: 'PUT'},
 *         {key: 'PATCH', value: 'PATCH'},
 *         {key: 'DELETE', value: 'DELETE'},
 *     ]}
 * />
 * ```
 */
export function Select({
    autoFocus,
    controlShouldRenderValue,
    components,
    defaultValue,
    disabled,
    filterOption,
    hideSelectedOptions,
    isClearable,
    isMulti,
    isSearchable,
    label,
    menuIsOpen,
    name,
    options,
    placeholder,
    prefix,
    raw,
    innerRef,
    size,
    statusBadge,
    suffix,
    validationError,
    value,
    onChange,
    onFocus,
    onBlur,
}) {
    const customComponents = {
        DropdownIndicator: CustomDropdownIndicator,
        IndicatorSeparator: CustomIndicatorSeparator,
        ...components,
    };
    return (
        <>
            <FormSelectListGlobalStyle />
            <FormContainer
                disabled={disabled}
                formSelect={true}
                label={label}
                prefix={prefix}
                errorMessage={
                    raw ? (
                        <>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>
                    ) : (
                        <ErrorMessage name={name} component={FormErrorMessage} />
                    )
                }
                size={size}
                statusBadge={statusBadge}
                suffix={suffix}>
                {raw ? (
                    <RawSelect
                        autoFocus={autoFocus}
                        components={customComponents}
                        controlShouldRenderValue={controlShouldRenderValue}
                        defaultValue={defaultValue}
                        filterOption={filterOption}
                        hideSelectedOptions={hideSelectedOptions}
                        disabled={disabled}
                        isClearable={isClearable}
                        isMulti={isMulti}
                        isSearchable={isSearchable}
                        options={options}
                        placeholder={placeholder}
                        menuIsOpen={menuIsOpen}
                        innerRef={innerRef}
                        value={value}
                        onChange={onChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                    />
                ) : (
                    <FormikSelect
                        autoFocus={autoFocus}
                        controlShouldRenderValue={controlShouldRenderValue}
                        defaultValue={defaultValue}
                        filterOption={filterOption}
                        hideSelectedOptions={hideSelectedOptions}
                        isClearable={isClearable}
                        isMulti={isMulti}
                        isSearchable={isSearchable}
                        name={name}
                        options={options}
                        disabled={disabled}
                        placeholder={placeholder}
                        menuIsOpen={menuIsOpen}
                        components={customComponents}
                        innerRef={innerRef}
                        onFocus={onFocus}
                        onChange={onChange}
                        onBlur={onBlur}
                    />
                )}
            </FormContainer>
        </>
    );
}
